import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Event } from '@shared/models/event.model';
import { BaseComponent } from '@shared/components/base.component';
import { RoutesPath } from '@shared/routes-path';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { EventStatus } from '@shared/interfaces/event/event.interface';
import { EventsService } from '@layouts/default/layouts/logged/pages/events/events.service';
import { Config } from '@shared/config';
import { EventsServicePublic } from '@layouts/default/layouts/guest/pages/events/events.service';

@Component({
  selector: 'app-event-box',
  templateUrl: './event-box.component.html',
  styleUrls: ['./event-box.component.scss'],
})
export class EventBoxComponent extends BaseComponent implements OnInit {
  @Input() event: Event;
  enumEventStatus: any = EventStatus;
  @Input() layout: string = 'grid';
  @Input() isProfile: boolean = false;
  @Input() isDraft: boolean = false;
  @Input() isHomepage: boolean = false;
  isClosed: boolean = false;
  @Output() deleteModalSuccess: EventEmitter<any> = new EventEmitter();
  shareUrl: string;
  meta: any;
  peopleInvited: number;
  currentUserJoined: boolean = false;
  displaydeleteModalDraft: boolean = false;

  constructor(
    public eventService: EventsService,
    public eventServicePublic: EventsServicePublic,
    public router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.shareUrl = window.location.origin + '/' + RoutesPath.default.events + '/' + this.event.id;
    this.meta = {
      title: this.event.name,
      description: this.event.name,
      image: this.event.image ? this.event.image : Config.PUBLIC_SHARE_DEFAULT_IMAGE,
    };

    this.isClosed = this.enumEventStatus.CLOSED === this.event.status;

    if (this.userAlreadyJoined()) {
      this.currentUserJoined = true;
    }

    this.peopleInvited = this.event.participant_counter;
  }

  userAlreadyJoined() {
    let joined = false;
    let email = this.storage.user ? this.storage.user.email : null;
    let invitations = this.event.invitation;
    if (invitations.length === 0) return false;

    for (let i = 0; i < invitations.length; i++) {
      if (email === invitations[i].email) {
        joined = true;
        break;
      }
    }
    return joined;
  }

  eventOnList(ids: Array<number>) {
    return !!ids.filter((id) => this.event.id === id).length;
  }

  getMonth() {
    return this.eventService.getMonth(this.event.start_at);
  }

  getDay() {
    return this.eventService.getDay(this.event.start_at);
  }

  getYear() {
    return this.eventService.getYear(this.event.start_at);
  }

  getStartTime() {
    return this.eventService.getTime(this.event.start_at);
  }

  getEndTime() {
    return this.eventService.getTime(this.event.end_at);
  }

  getTime() {
    let string = '';
    string += this.getStartTime();
    if (this.event.end_at) {
      string += ' - ' + this.getEndTime();
    }
    return string;
  }

  getAddress() {
    const { address } = this.event;
    let string = [];
    if (address.street) {
      string.push(address.street);
    }
    if (address.city) {
      string.push(address.city);
    }
    if (address.country) {
      string.push(this.translate.instant('COUNTRIES.' + address.country));
    }

    return string.join(', ');
  }

  getImage() {
    if (this.event.theme) {
      return Config.HOST + this.event.theme.image;
    } else if (this.event.file) {
      return Config.HOST + this.event.file.image;
    } else {
      return '/assets/img/img-placeholder-event.webp';
    }
  }

  isLessThan7Days() {
    let startAt = moment(this.event.start_at, 'YYYY-MM-DD');
    let today = moment();
    return startAt.diff(today, 'days') <= 7;
  }

  datesClasses() {
    return {
      lessThan7Days: this.isLessThan7Days() && this.event.status !== this.enumEventStatus.CLOSED,
    };
  }

  boxClasses() {
    return {
      isClosed: this.isClosed,
    };
  }

  joinEvent() {
    if (!this.storage.user) {
      this.router.navigate([], { queryParams: this.authPage('login') });
      return;
    }

    const body = {
      event_invitation: {
        event: this.event.id,
        invitations: this.storage.user.email,
        accepted: true,
        inviting: this.storage.user.id,
        listInvite: false,
        children: 0,
      },
    };
    const urlType = this.eventServicePublic.addParticipants(body);

    const addedParticipants = urlType.subscribe((res) => {
      this.toast.success(this.translate.instant('EVENT.added_to_event'));
      this.peopleInvited += 1;
      this.currentUserJoined = true;
    }, this.onGetParticipantsError.bind(this));
    this.someSubscriptions.add(addedParticipants);
  }

  onGetParticipantsError(error: any) {
    var message = this.translate.instant('EVENT.event_error_message');
    if (error.data && error.data.errors[0]) {
      message = error.data.errors[0];
    }
    this.toast.error(message, this.translate.instant('EVENT.event_error'));
  }

  eventDeleteDraft() {
    const subDeleteDraftEvent = this.eventService.deleteDraftEvent(this.event.id).subscribe(() => {
      this.toast.success(
        this.translate.instant('EVENT.event_success'),
        this.translate.instant('EVENT.event_deleted')
      );
      this.closeDeleteModalDraft();
      this.deleteModalSuccess.emit();
    }, this.onEndEventError.bind(this));
    this.someSubscriptions.add(subDeleteDraftEvent);
  }

  onEndEventError(error: Error) {
    this.toast.error(
      this.translate.instant('EVENT.event_error'),
      this.translate.instant('EVENT.not_success_end_event')
    );
  }

  dateFromNow() {
    return moment(
      this.event.created_at,
      Config.DATE_FORMAT_BACKEND,
      this.storage.getLang()
    ).fromNow();
  }

  goToEdit(event) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/' + RoutesPath.default.event_edit + '/' + this.event.id]);
  }

  hasMCDTheme(event: Event) {
    return event.theme && event.theme.id == 19;
  }

  displayJoinButton(event: Event) {
    return (
      (this.storage.user &&
        this.storage.user.id !== event.user.id &&
        event.type !== 'PrivateEvent' &&
        !event.categories.some((e) => e.id === 29) &&
        !this.hasMCDTheme(event)) ||
      (!this.storage.user &&
        event.type !== 'PrivateEvent' &&
        !event.categories.some((e) => e.id === 29) &&
        !this.hasMCDTheme(event))
    );
  }

  public closeDeleteModalDraft(): void {
    this.displaydeleteModalDraft = false;
  }

  public openDeleteModalDraft(event): void {
    event.preventDefault();
    event.stopPropagation();
    this.displaydeleteModalDraft = true;
  }
}
